import { State, Action, Selector, StateContext } from '@ngxs/store';
import { DecodedToken, LastLoggedInUser, SnackbarType } from '@core/models';
import {
  LoginAction,
  LoginSuccessAction,
  LoginFailAction,
  ClearTokenAction,
  ClearLastUserAction,
  RequestForgotenPasswordAction,
  RequestForgotenPasswordSuccessAction,
  RequestForgotenPasswordFailAction,
  ResetForgotenPasswordAction,
  ResetForgotenPasswordSuccessAction,
  ResetForgotenPasswordFailAction,
  ChangeTenantAction,
  LoadHasAuthenticatorConfiguredAction,
  LoadHasAuthenticatorConfiguredSuccessAction,
  LoadHasAuthenticatorConfiguredFailAction,
  RegisterAuthenticatorAction,
  RegisterAuthenticatorSuccessAction,
  RegisterAuthenticatorFailAction,
  UnRegisterAuthenticatorAction,
  UnRegisterAuthenticatorSuccessAction,
  UnRegisterAuthenticatorFailAction,
  UnlockAccountWithAuthenticatorAction,
  UnlockAccountWithAuthenticatorSuccessAction,
  UnlockAccountWithAuthenticatorFailAction,
  CreateAuthenticatorPinRequestAction,
  CreateAuthenticatorPinRequestSuccessAction,
  CreateAuthenticatorPinRequestFailAction,
  EnableDisable2FAAction,
  EnableDisable2FASuccessAction,
  EnableDisable2FAFailAction,
  Has2FAEnabledAction,
  Has2FAEnabledSuccessAction,
  Has2FAEnabledFailAction,
  ResetPasswordWithAuthenticatorAction,
  ResetPasswordWithAuthenticatorSuccessAction,
  ResetPasswordWithAuthenticatorFailAction,
  LoadIsAccountLockedAction,
  LoadIsAccountLockedSuccessAction,
  LoadIsAccountLockedFailAction,
  UnlockAccountAction,
  UnlockAccountSuccessAction,
  UnlockAccountFailAction
} from './auth.actions';
import { tap, catchError } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { OpenSnackbarAction } from '../ui-store/ui.actions';
import { Navigate } from '@ngxs/router-plugin';
import { environment } from '@environments/environment';
import { Injectable } from '@angular/core';
import { UsersClient, IssuedTokenModel, QrCodeSetupModel, PinRequestModel } from '@core/services/api.service';
import { TenantModel } from '@core/models/auth/tenant.model';
import { LoadTourListAction } from '@ngxs-store/managed-tour-store/managed-tour.actions';
import { of } from 'rxjs';
import { DeepLinkService } from '@core/services/deep-link.service';

export class AuthStateModel {
  public isLoading: boolean;
  public lastLoggedInUser: LastLoggedInUser;
  public hasAuthenticatorConfigured: boolean;
  public qrCodeSetupModel: QrCodeSetupModel;
  pinRequestModel: PinRequestModel;
  has2FAEnabled: boolean;
  resetPasswordSuccess: boolean;
  public decodedToken: DecodedToken;
  public isAccountLocked: boolean | null;
  public tenant: TenantModel;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    isLoading: false,
    lastLoggedInUser: { username: '', firstName: '', surname: '' },
    hasAuthenticatorConfigured: false,
    pinRequestModel: null,
    has2FAEnabled: false,
    resetPasswordSuccess: false,
    isAccountLocked: null,
    qrCodeSetupModel: null,
    decodedToken: {
      firstName: '',
      expires: null,
      roles: [],
      surname: '',
      token: '',
      username: '',
      uid: '',
      tenants: []
    },
    tenant: {
      id: '',
      name: ''
    }
  }
})
@Injectable()
export class AuthState {
  jwtHelper = new JwtHelperService();
  authorized: boolean;

  @Selector()
  static getIsLoading(state: AuthStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static getDecodedToken(state: AuthStateModel): DecodedToken {
    return state.decodedToken;
  }

  @Selector()
  static getLastLoggedInUser(state: AuthStateModel): LastLoggedInUser {
    return state.lastLoggedInUser;
  }

  @Selector()
  static getTenant(state: AuthStateModel): TenantModel {
    return state.tenant;
  }

  @Selector()
  static getHasAuthenticatorConfigured(state: AuthStateModel): boolean {
    return state.hasAuthenticatorConfigured;
  }

  @Selector()
  static getQrCodeSetupModel(state: AuthStateModel): QrCodeSetupModel {
    return state.qrCodeSetupModel;
  }

  @Selector()
    static pinRequestModel(state: AuthStateModel): PinRequestModel | null {
        return state.pinRequestModel;
    }

  @Selector()
    static has2FAEnabled(state: AuthStateModel): boolean {
        return state.has2FAEnabled;
    }

  @Selector()
    static resetPasswordSuccess(state: AuthStateModel): boolean {
      return state.resetPasswordSuccess;
    }


  @Selector()
  static getHasValidToken(state: AuthStateModel): boolean {
    return state.decodedToken && state.decodedToken.token && state.decodedToken.expires && new Date() < new Date(state.decodedToken.expires);
  }

  @Selector()
  static getHasRole(state: AuthStateModel) {
    return (requiredRoles: string[]) => {
      return state.decodedToken && state.decodedToken.roles && requiredRoles.some(role => state.decodedToken.roles.includes(role));
    };
  }

  constructor(
    private usersClient: UsersClient,
    private deepLinkService: DeepLinkService
  ) { }

  @Action(LoginAction)
  loginAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginAction) {
    patchState({ isLoading: true });

    return this.usersClient.authenticateUser(action.payload.authenticateUserCommand).pipe(
      tap((encodedToken) => dispatch(new LoginSuccessAction({ decodedToken: this.decodeToken(encodedToken), redirectUrl: action.payload.redirectUrl }))),
      catchError((err) => dispatch(new LoginFailAction()))
    );
  }

  @Action(LoginSuccessAction)
  loginSuccessAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginSuccessAction) {

    const firstTenant = action.payload.decodedToken.tenants[0];
    const tenant = { id: firstTenant.split('|')[0], name: firstTenant.split('|')[1] };
    dispatch(new LoadTourListAction());
    patchState({
      decodedToken: action.payload.decodedToken,
      lastLoggedInUser: {
        username: action.payload.decodedToken.username,
        firstName: action.payload.decodedToken.firstName,
        surname: action.payload.decodedToken.surname
      },
      isLoading: false,
      tenant
    });

    // Check for stored return URL from auth guard first
    const storedReturnUrl = sessionStorage.getItem('auth_return_url');
    if (storedReturnUrl) {
      console.log(`AuthState: Restoring return URL after login: ${storedReturnUrl}`);
      sessionStorage.removeItem('auth_return_url'); // Clear it
      
      // Parse URL to separate path and query params
      const urlParts = storedReturnUrl.split('?');
      const path = urlParts[0];
      const queryString = urlParts[1];
      
      if (queryString) {
        // Parse query params into an object
        const queryParams = {};
        queryString.split('&').forEach(param => {
          const [key, value] = param.split('=');
          queryParams[key] = decodeURIComponent(value);
        });
        
        console.log(`AuthState: Navigating to ${path} with query params:`, queryParams);
        dispatch(new Navigate([path], null, { queryParams }));
      } else {
        console.log(`AuthState: Navigating to ${path}`);
        dispatch(new Navigate([path]));
      }
      
      dispatch(
        new OpenSnackbarAction({
          message: `Welcome ${action.payload.decodedToken.firstName} ${action.payload.decodedToken.surname}`,
          type: SnackbarType.Simple
        })
      );
      return; // Exit early
    }

    // Check for stored deep-link URL
    const storedDeepLinkUrl = this.deepLinkService.getStoredDeepLinkUrl();
    if (storedDeepLinkUrl) {
      console.log(`AuthState: Restoring deep-link URL after login: ${storedDeepLinkUrl}`);

      // Extract just the base path for navigation (without parameters)
      try {
        const url = new URL(storedDeepLinkUrl);
        let path = url.pathname;

        // Remove any semicolon parameters from the path
        if (path.includes(';')) {
          path = path.split(';')[0];
        }

        console.log(`AuthState: Navigating to base path: ${path}`);
        dispatch(new Navigate([path]));

        // Don't clear the stored URL yet - let the component handle it
        // The deep-link parameters will be handled by the component when it initializes
      } catch (error) {
        // If URL parsing fails, try to extract path manually
        const toursIndex = storedDeepLinkUrl.indexOf('/tours');
        if (toursIndex !== -1) {
          // Extract just the base path, not the parameters
          const fullPath = storedDeepLinkUrl.substring(toursIndex);
          const basePath = fullPath.split(';')[0]; // Remove parameters

          console.log(`AuthState: Navigating to base path: ${basePath}`);
          dispatch(new Navigate([basePath]));
        } else {
          // Fallback to default navigation
          console.log(`AuthState: Fallback navigation - no valid tours path found`);
          this.deepLinkService.clearStoredDeepLinkUrl(); // Clear invalid URL
          if (action.payload.decodedToken.roles[0] == "Customer") {
            dispatch(new Navigate(['/my-tours']));
          } else if (action.payload.redirectUrl) {
            dispatch(new Navigate([action.payload.redirectUrl]));
          }
        }
      }
    } else if (action.payload.decodedToken.roles[0] == "Customer") {
      dispatch(new Navigate(['/my-tours']));
    } else if (action.payload.redirectUrl) {
      dispatch(new Navigate([action.payload.redirectUrl]));
    }

    dispatch(
      new OpenSnackbarAction({
        message: `Welcome ${action.payload.decodedToken.firstName} ${action.payload.decodedToken.surname}`,
        type: SnackbarType.Simple
      })
    );
  }

  @Action(LoginFailAction)
  loginFailAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoginFailAction) {
    patchState({
      decodedToken: {
        firstName: '',
        expires: null,
        roles: [],
        surname: '',
        token: '',
        username: '',
        uid: '',
        tenants: []
      },
      isLoading: false,
      tenant: {
        id: '',
        name: ''
      }
    });
  }

  @Action(ClearTokenAction)
  clearTokenAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ClearTokenAction) {
    patchState({
      decodedToken: {
        firstName: '',
        expires: null,
        roles: [],
        surname: '',
        token: '',
        username: '',
        uid: '',
        tenants: []
      },
      tenant: {
        id: '',
        name: ''
      }
    });

    if (environment.routeOnLogout.enabled) {
      localStorage.removeItem('Tour');
      dispatch(new Navigate(environment.routeOnLogout.url));
    }
  }

  @Action(ClearLastUserAction)
  clearLastUserAction({ patchState }: StateContext<AuthStateModel>, action: ClearLastUserAction) {
    patchState({
      lastLoggedInUser: { username: '', firstName: '', surname: '' }
    });
  }

  decodeToken(issuedTokenModel: IssuedTokenModel): DecodedToken {
    const decoded = this.jwtHelper.decodeToken(issuedTokenModel.token);

    return {
      expires: new Date(decoded.exp * 1000).toISOString(),
      firstName: decoded.firstName,
      roles: decoded.roles instanceof Array ? decoded.roles : [decoded.roles],
      username: decoded.sub.replace('SUPERGRP\\', ''),
      surname: decoded.surname,
      token: issuedTokenModel.token,
      uid: decoded.uid,
      tenants: decoded.tenants instanceof Array ? decoded.tenants : [decoded.tenants]
    };
  }

  @Action(RequestForgotenPasswordAction)
  requestForgotenPasswordAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: RequestForgotenPasswordAction) {
    patchState({ isLoading: true });

    return this.usersClient.requestPasswordReset(action.payload.requestPasswordResetCommand).pipe(
      tap((email) => dispatch(new RequestForgotenPasswordSuccessAction({ email: email.emailAddress }))),
      catchError((err) => dispatch(new RequestForgotenPasswordFailAction()))
    );
  }

  @Action(RequestForgotenPasswordSuccessAction)
  requestForgotenPasswordSuccessAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: RequestForgotenPasswordSuccessAction) {
    patchState({
      isLoading: false
    });

    dispatch(new Navigate(['/auth/login']));
    dispatch(new OpenSnackbarAction({ message: 'Password reset email sent.', type: SnackbarType.Simple }));
  }

  @Action(RequestForgotenPasswordFailAction)
  requestForgotenPasswordFailAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: RequestForgotenPasswordFailAction) {
    patchState({
      isLoading: false
    });
  }

  @Action(ResetForgotenPasswordAction)
  resetForgotenPasswordAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: ResetForgotenPasswordAction) {
    patchState({ isLoading: true });

    return this.usersClient.resetPassword(action.payload.resetPasswordCommand).pipe(
      tap((username) => dispatch(new ResetForgotenPasswordSuccessAction({ username: username.username }))),
      catchError((err) => dispatch(new ResetForgotenPasswordFailAction()))
    );
  }

  @Action(ResetForgotenPasswordSuccessAction)
  resetForgotenPasswordSuccessAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ResetForgotenPasswordSuccessAction) {
    patchState({
      isLoading: false
    });

    dispatch(new Navigate(['/auth/login']));
    dispatch(new OpenSnackbarAction({ message: 'Password successfully reset.', type: SnackbarType.Simple }));
  }

  @Action(ResetForgotenPasswordFailAction)
  resetForgotenPasswordFailAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ResetForgotenPasswordFailAction) {
    patchState({
      isLoading: false
    });
  }

  @Action(ChangeTenantAction)
  changeTenantAction({ dispatch, getState, patchState }: StateContext<AuthStateModel>, action: ChangeTenantAction) {
    const state = getState();
    patchState({
      tenant: action.payload.tenant
    });

    location.reload();
  }

  //HasAuthConfigured
  @Action(LoadHasAuthenticatorConfiguredAction)
  LoadHasAuthenticatorConfiguredAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoadHasAuthenticatorConfiguredAction) {
    patchState({ isLoading: true });

    return this.usersClient.hasAuthenticatorConfigured(action.payload.username).pipe(
      tap((hasAuthenticatorConfigured) => dispatch(new LoadHasAuthenticatorConfiguredSuccessAction({ hasAuthenticatorConfigured }))),
      catchError((err) => dispatch(new LoadHasAuthenticatorConfiguredFailAction()))
    );
  }

  @Action(LoadHasAuthenticatorConfiguredSuccessAction)
  LoadHasAuthenticatorConfiguredSuccessAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoadHasAuthenticatorConfiguredSuccessAction) {
    patchState({
      hasAuthenticatorConfigured: action.payload.hasAuthenticatorConfigured,
      isLoading: false
    });
  }

  @Action(LoadHasAuthenticatorConfiguredFailAction)
  LoadHasAuthenticatorConfiguredFailAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: LoadHasAuthenticatorConfiguredFailAction) {
    patchState({
      hasAuthenticatorConfigured: false,
      isLoading: false
    });
  }

  //Register Authenticater
  @Action(RegisterAuthenticatorAction)
  RegisterAuthenticatorAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: RegisterAuthenticatorAction) {
    patchState({ isLoading: true });

    return this.usersClient.registerAuthenticator(action.payload.username).pipe(
      tap((qrCodeSetupModel) => dispatch(new RegisterAuthenticatorSuccessAction({ qrCodeSetupModel }))),
      catchError((err) => dispatch(new RegisterAuthenticatorFailAction()))
    );
  }

  @Action(RegisterAuthenticatorSuccessAction)
  RegisterAuthenticatorSuccessAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: RegisterAuthenticatorSuccessAction) {
    patchState({
      qrCodeSetupModel: action.payload.qrCodeSetupModel,
      isLoading: false
    });
  }

  @Action(RegisterAuthenticatorFailAction)
  RegisterAuthenticatorFailAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: RegisterAuthenticatorFailAction) {
    patchState({
      qrCodeSetupModel: null,
      isLoading: false
    });
  }


  //UnregisterAuthenticator
  @Action(UnRegisterAuthenticatorAction)
  UnRegisterAuthenticatorAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: UnRegisterAuthenticatorAction) {
    patchState({ isLoading: true });

    return this.usersClient.unRegisterAuthenticator(action.payload.username).pipe(
      tap(() => dispatch(new UnRegisterAuthenticatorSuccessAction())),
      catchError((err) => dispatch(new UnRegisterAuthenticatorFailAction()))
    );
  }

  @Action(UnRegisterAuthenticatorSuccessAction)
  UnRegisterAuthenticatorSuccessAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: UnRegisterAuthenticatorSuccessAction) {
    patchState({
      qrCodeSetupModel: null,
      hasAuthenticatorConfigured: false,
      isLoading: false
    });
    dispatch(new OpenSnackbarAction({ message: 'User unregistered successfully', type: SnackbarType.Simple }));
  }

  @Action(UnRegisterAuthenticatorFailAction)
  UnRegisterAuthenticatorFailAction({ dispatch, patchState }: StateContext<AuthStateModel>, action: UnRegisterAuthenticatorFailAction) {
    patchState({
      qrCodeSetupModel: null,
      isLoading: false
    });
    dispatch(new OpenSnackbarAction({ message: 'User unregistered failed', type: SnackbarType.Simple }));
  }

  //login with auth

  @Action(UnlockAccountWithAuthenticatorAction)
  unlockAccountWithAuthenticatorAction({ getState, dispatch, patchState }: StateContext<AuthStateModel>, action: UnlockAccountWithAuthenticatorAction) {
      patchState({ isLoading: true });

      return this.usersClient.unlockAccountWithAuthenticator(action.payload.pin, action.payload.key).pipe(
          tap(() => dispatch(new UnlockAccountWithAuthenticatorSuccessAction())),
          catchError((err) => {
              dispatch(new UnlockAccountWithAuthenticatorFailAction());
              return of(err);
          })
      );
  }

  @Action(UnlockAccountWithAuthenticatorSuccessAction)
    unlockAccountWithAuthenticatorSuccessAction({ patchState, dispatch }: StateContext<AuthStateModel>) {
        patchState({ isLoading: false });
        const authorized: boolean = true;
        dispatch(new Navigate(['/auth/login'], { queryParams: authorized }));
    }


  @Action(UnlockAccountWithAuthenticatorFailAction)
  unlockAccountWithAuthenticatorFailAction({ patchState }: StateContext<AuthStateModel>) {
      patchState({ isLoading: false });
  }

  //pin request
  @Action(CreateAuthenticatorPinRequestAction)
  createAuthenticatorPinRequestAction({ patchState, dispatch }: StateContext<AuthStateModel>, action: CreateAuthenticatorPinRequestAction) {
      patchState({ isLoading: true });

      return this.usersClient.createAuthenticatorPinRequest(action.payload.username).pipe(
          tap((pinRequestModel: PinRequestModel) => dispatch(new CreateAuthenticatorPinRequestSuccessAction({ pinRequestModel }))),
          catchError((error) => of(dispatch(new CreateAuthenticatorPinRequestFailAction())))
      );
  }

  @Action(CreateAuthenticatorPinRequestSuccessAction)
  createAuthenticatorPinRequestSuccessAction({ patchState }: StateContext<AuthStateModel>, action: CreateAuthenticatorPinRequestSuccessAction) {
      patchState({
          pinRequestModel: action.payload.pinRequestModel,
          isLoading: false
      });
  }

  @Action(CreateAuthenticatorPinRequestFailAction)
  createAuthenticatorPinRequestFailAction({ patchState }: StateContext<AuthStateModel>) {
      patchState({
          isLoading: false
      });
  }


  @Action(EnableDisable2FAAction)
  enableDisable2FAAction({ patchState, dispatch }: StateContext<AuthStateModel>, action: EnableDisable2FAAction) {
    patchState({ isLoading: true });

    return this.usersClient.enableDisable2FA(action.payload.username, action.payload.enabled).pipe(
      tap(() => dispatch(new EnableDisable2FASuccessAction())),
      catchError((err) => {
        dispatch(new EnableDisable2FAFailAction());
        return of(err);
      })
    );
  }

  @Action(EnableDisable2FASuccessAction)
  enableDisable2FASuccessAction({ patchState }: StateContext<AuthStateModel>) {
    patchState({ isLoading: false });
  }

  @Action(EnableDisable2FAFailAction)
  enableDisable2FAFailAction({ patchState }: StateContext<AuthStateModel>) {
    patchState({ isLoading: false });
  }

  @Action(Has2FAEnabledAction)
  has2FAEnabledAction({ getState, patchState, dispatch }: StateContext<AuthStateModel>, action: Has2FAEnabledAction) {
    patchState({ isLoading: true });

    return this.usersClient.has2FAEnabled(action.payload.username).pipe(
      tap((has2FAEnabled: boolean) => {
        dispatch(new Has2FAEnabledSuccessAction({ has2FAEnabled }));
      }),
      catchError((err) => {
        dispatch(new Has2FAEnabledFailAction());
        return of(err);
      })
    );
  }

  @Action(Has2FAEnabledSuccessAction)
  has2FAEnabledSuccessAction({ patchState }: StateContext<AuthStateModel>, action: Has2FAEnabledSuccessAction) {
    patchState({
      has2FAEnabled: action.payload.has2FAEnabled,
      isLoading: false
    });
  }

  @Action(Has2FAEnabledFailAction)
  has2FAEnabledFailAction({ patchState }: StateContext<AuthStateModel>) {
    patchState({
      isLoading: false
    });
  }


  @Action(ResetPasswordWithAuthenticatorAction)
  resetPasswordWithAuthenticatorAction({ getState, patchState, dispatch }: StateContext<AuthStateModel>, action: ResetPasswordWithAuthenticatorAction) {
      patchState({ isLoading: true });

      return this.usersClient.resetPasswordWithAuthenticator(action.payload.pin, action.payload.key).pipe(
          tap(() => dispatch(new ResetPasswordWithAuthenticatorSuccessAction())),
          catchError((err) => {
              dispatch(new ResetPasswordWithAuthenticatorFailAction());
              return of(err);
          })
      );
  }

  @Action(ResetPasswordWithAuthenticatorSuccessAction)
  resetPasswordWithAuthenticatorSuccessAction({ patchState }: StateContext<AuthStateModel>) {
      patchState({
          isLoading: false,
          resetPasswordSuccess: true
      });
  }

  @Action(ResetPasswordWithAuthenticatorFailAction)
  resetPasswordWithAuthenticatorFailAction({ patchState }: StateContext<AuthStateModel>) {
      patchState({
          isLoading: false,
          resetPasswordSuccess: false
      });
  }

    @Action(LoadIsAccountLockedAction)
    loadIsAccountLocked({ getState, patchState, dispatch }: StateContext<AuthStateModel>, action: Has2FAEnabledAction) {
    patchState({ isLoading: true });

    return this.usersClient.isAccountLocked(action.payload.username).pipe(
      tap((isAccountLocked: boolean) => {
        dispatch(new LoadIsAccountLockedSuccessAction({ isAccountLocked }));
      }),
      catchError((err) => {
        dispatch(new LoadIsAccountLockedFailAction());
        return of(err);
      })
    );
  }

  @Action(LoadIsAccountLockedSuccessAction)
  loadIsAccountLockedSuccess({ patchState, dispatch }: StateContext<AuthStateModel>, action: LoadIsAccountLockedSuccessAction) {
    patchState({
      isAccountLocked: action.payload.isAccountLocked,
      isLoading: false
    });
    if (action.payload.isAccountLocked) {
        dispatch(new OpenSnackbarAction({ message: 'Account locked due to too many incorrect 2FA PIN attempts', type: SnackbarType.Simple }));
    }
  }

  @Action(LoadIsAccountLockedFailAction)
  loadIsAccountLockedFail({ patchState }: StateContext<AuthStateModel>) {
    patchState({
      isLoading: false
    });
  }


  //unlock account
  @Action(UnlockAccountAction)
    unlockAccount({ getState, patchState, dispatch }: StateContext<AuthStateModel>, action: UnlockAccountAction) {
        patchState({ isLoading: true });

        return this.usersClient.unlockAccount(action.payload.username, action.payload.islocked).pipe(
            tap(() => {
                dispatch(new UnlockAccountSuccessAction());
            }),
            catchError((err) => {
                dispatch(new UnlockAccountFailAction());
                return of(err);
            })
        );
    }

    @Action(UnlockAccountSuccessAction)
    unlockAccountSuccess({ patchState, dispatch }: StateContext<AuthStateModel>) {
        patchState({ isLoading: false, isAccountLocked: false });

        dispatch(new OpenSnackbarAction({ message: 'Account successfully unlocked', type: SnackbarType.Simple }));
    }

    @Action(UnlockAccountFailAction)
    unlockAccountFail({ patchState, dispatch }: StateContext<AuthStateModel>) {
        patchState({ isLoading: false });

        dispatch(new OpenSnackbarAction({ message: 'Failed to unlock account', type: SnackbarType.Simple }));
    }

}
